import React, { useEffect, useState, useMemo } from 'react';
import { DataGrid }  from '@mui/x-data-grid';
import { esES }      from '@mui/x-data-grid/locales';
import { Container, Typography, Button, Box, Paper, Tabs, Tab, Chip } from '@mui/material';
import { api }       from '../services/api';
import * as XLSX     from 'xlsx';
import { Line, Bar, Pie, Doughnut } from 'react-chartjs-2';
import 'chart.js/auto';
import UsersTable    from './UsersTable';

const COLORS = [
  '#1976d2','#2e7d32','#d32f2f','#ed6c02','#9c27b0',
  '#0288d1','#c2185b','#00796b','#5d4037','#f57f17',
];

export default function SessionsPage() {
  const [rows,    setRows]    = useState([]);
  const [reportes,setReportes]= useState([]);
  const [tab,     setTab]     = useState(0);

  useEffect(() => {
    api.get('/sesiones').then(({ data }) => {
      setRows(data.map(s => {
        const c = s.cliente ?? {};
        return {
          id:          s.id,
          clienteNom:  `${c.nombre??''} ${c.ap_paterno??''} ${c.ap_materno??''}`.trim(),
          email:       c.email || '',
          inicio:      s.loginAt  ? new Date(s.loginAt ).toLocaleString('es-PE') : '',
          fin:         s.logoutAt ? new Date(s.logoutAt).toLocaleString('es-PE') : '',
          duracion:    s.duracionSeg != null ? (s.duracionSeg/60).toFixed(1) : '',
          rawLoginAt:  s.loginAt || '',
        };
      }));
    }).catch(console.error);

    api.get('/reportes').then(({ data }) => {
      setReportes(data.map(r => ({
        id:       r.id,
        usuario:  r.usuario,
        tipo:     r.tipo_reporte,
        alimento: r.alimento || '—',
        tipoalimento: r.tipoalimento || '—',
        tiporesiduo:  r.tiporesiduo  || '—',
        analito:      r.analito      || '—',
        fecha:    r.fecha_generacion
          ? new Date(r.fecha_generacion).toLocaleString('es-PE')
          : '',
      })));
    }).catch(console.error);
  }, []);

  /* ── columnas sesiones ── */
  const colsSesiones = [
    { field: 'id',         headerName: 'ID',      width: 70  },
    { field: 'clienteNom', headerName: 'Cliente', flex: 1, minWidth: 200 },
    { field: 'email',      headerName: 'Correo',  flex: 1, minWidth: 180 },
    { field: 'inicio',     headerName: 'Inicio',  width: 180 },
    { field: 'fin',        headerName: 'Fin',     width: 180 },
    { field: 'duracion',   headerName: 'Duración (min)', width: 130 },
  ];

  /* ── columnas reportes ── */
  const colsReportes = [
    { field: 'id',      headerName: 'ID',      width: 70 },
    { field: 'usuario', headerName: 'Usuario', flex: 1, minWidth: 180 },
    {
      field: 'tipo',
      headerName: 'Tipo',
      width: 100,
      renderCell: (p) => (
        <Chip
          label={p.value}
          size="small"
          color={p.value === 'PDF' ? 'error' : 'success'}
          variant="outlined"
        />
      ),
    },
    { field: 'alimento',     headerName: 'Alimento',      flex: 1, minWidth: 140 },
    { field: 'tipoalimento', headerName: 'Tipo alimento', flex: 1, minWidth: 160 },
    { field: 'tiporesiduo',  headerName: 'Tipo residuo',  flex: 1, minWidth: 200 },
    { field: 'analito',      headerName: 'Analito',       flex: 1, minWidth: 140 },
    { field: 'fecha',        headerName: 'Fecha y hora',  width: 180 },
  ];

  /* ── exportar Excel sesiones ── */
  const exportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(rows.map(
      ({ id, clienteNom, email, inicio, fin, duracion }) =>
        ({ ID: id, Cliente: clienteNom, Correo: email, Inicio: inicio, Fin: fin, 'Duración (min)': duracion })
    ));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sesiones');
    XLSX.writeFile(wb, 'sesiones.xlsx');
  };

  /* ── exportar Excel reportes ── */
  const exportExcelReportes = () => {
    const ws = XLSX.utils.json_to_sheet(reportes.map(
      ({ id, usuario, tipo, alimento, tipoalimento, tiporesiduo, analito, fecha }) =>
        ({ ID: id, Usuario: usuario, Tipo: tipo, Alimento: alimento,
           'Tipo alimento': tipoalimento, 'Tipo residuo': tiporesiduo,
           Analito: analito, 'Fecha y hora': fecha })
    ));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Reportes');
    XLSX.writeFile(wb, 'historial_reportes.xlsx');
  };

  /* ── gráficos ── */
  const { lineData, barData, pieData, statsCards } = useMemo(() => {
    const today = new Date(), days = [];
    for (let i = 13; i >= 0; i--) {
      const d = new Date(today); d.setDate(today.getDate() - i);
      days.push(d.toISOString().slice(0, 10));
    }
    const perDay = days.map(d => rows.filter(r => r.rawLoginAt?.startsWith(d)).length);

    const from = new Date(); from.setDate(from.getDate() - 7);
    const map = {};
    rows.filter(r => new Date(r.rawLoginAt) >= from)
        .forEach(r => { map[r.clienteNom] = (map[r.clienteNom] || 0) + 1; });
    const top = Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 10);

    // Pie top 10 + Otros
    const all = {};
    rows.forEach(r => { all[r.email] = (all[r.email] || 0) + 1; });
    const sorted = Object.entries(all).sort((a, b) => b[1] - a[1]);
    const top10  = sorted.slice(0, 10);
    const otros  = sorted.slice(10).reduce((s, [, v]) => s + v, 0);
    const pieLabels = [...top10.map(t => t[0]), ...(otros > 0 ? ['Otros'] : [])];
    const pieValues = [...top10.map(t => t[1]), ...(otros > 0 ? [otros]  : [])];
    const pieColors = [...COLORS, '#bdbdbd'];

    const statsCards = {
      totalSesiones:  rows.length,
      hoy:            rows.filter(r => r.rawLoginAt?.startsWith(today.toISOString().slice(0,10))).length,
      usuariosUnicos: new Set(rows.map(r => r.email)).size,
      durPromedio:    rows.filter(r => r.duracion).length > 0
        ? (rows.filter(r => r.duracion).reduce((s, r) => s + parseFloat(r.duracion), 0)
           / rows.filter(r => r.duracion).length).toFixed(1)
        : '—',
    };

    return {
      lineData: {
        labels: days.map(d => d.slice(5)), // MM-DD
        datasets: [{ label: 'Sesiones por día', data: perDay,
          borderColor: COLORS[0], backgroundColor: COLORS[0] + '22',
          tension: 0.4, fill: true, pointRadius: 4 }],
      },
      barData: {
        labels: top.map(t => t[0].split(' ').slice(0,2).join(' ')), // nombre corto
        datasets: [{ label: 'Logins últimos 7 días', data: top.map(t => t[1]),
          backgroundColor: top.map((_, i) => COLORS[i % COLORS.length] + 'aa'),
          borderColor:     top.map((_, i) => COLORS[i % COLORS.length]),
          borderWidth: 2, borderRadius: 6 }],
      },
      pieData: {
        labels: pieLabels,
        datasets: [{ data: pieValues, backgroundColor: pieColors,
          borderWidth: 1, borderColor: '#fff' }],
      },
      statsCards,
    };
  }, [rows]);

  /* ── UI ── */
  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>

      {/* Tarjetas resumen */}
      <Box display="flex" gap={2} mb={4} flexWrap="wrap">
        {[
          { label: 'Total sesiones',    value: statsCards?.totalSesiones,  color: '#1976d2', icon: '📊' },
          { label: 'Sesiones hoy',      value: statsCards?.hoy,            color: '#2e7d32', icon: '📅' },
          { label: 'Usuarios únicos',   value: statsCards?.usuariosUnicos, color: '#9c27b0', icon: '👥' },
          { label: 'Duración prom(min)',value: statsCards?.durPromedio,    color: '#ed6c02', icon: '⏱️' },
        ].map((c, i) => (
          <Paper key={i} elevation={2} sx={{
            flex: '1 1 180px', p: 2.5, borderRadius: 3,
            borderLeft: `5px solid ${c.color}`,
            display: 'flex', alignItems: 'center', gap: 2,
          }}>
            <span style={{ fontSize: '2rem' }}>{c.icon}</span>
            <Box>
              <Typography variant="h4" fontWeight={700} color={c.color}>{c.value}</Typography>
              <Typography variant="caption" color="text.secondary">{c.label}</Typography>
            </Box>
          </Paper>
        ))}
      </Box>

      {/* Tabs */}
      <Paper elevation={1} sx={{ borderRadius: 3, mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2 }}>
          <Tab label="📋 Sesiones de usuarios" />
          <Tab label="📄 Historial de reportes" />
          <Tab label="📈 Estadísticas" />
          <Tab label="👥 Registro de usuarios" />
        </Tabs>
      </Paper>

      {/* Tab 0: Sesiones */}
      {tab === 0 && (
        <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" p={2}
            sx={{ borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="h6" fontWeight={600}>Sesiones de clientes</Typography>
            <Button variant="contained" size="small" onClick={exportExcel}
              sx={{ borderRadius: 2 }}>
              📥 Exportar Excel
            </Button>
          </Box>
          <Box sx={{ height: 500 }}>
            <DataGrid rows={rows} columns={colsSesiones}
              pageSizeOptions={[25, 100]}
              localeText={esES.components.MuiDataGrid.defaultProps.localeText}
              density="compact" />
          </Box>
        </Paper>
      )}

      {/* Tab 1: Historial reportes */}
      {tab === 1 && (
        <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" p={2}
            sx={{ borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="h6" fontWeight={600}>Historial de reportes generados</Typography>
            <Button variant="contained" size="small" color="success" onClick={exportExcelReportes}
              sx={{ borderRadius: 2 }}>
              📥 Exportar Excel
            </Button>
          </Box>
          <Box sx={{ height: 500 }}>
            <DataGrid rows={reportes} columns={colsReportes}
              pageSizeOptions={[25, 100]}
              localeText={esES.components.MuiDataGrid.defaultProps.localeText}
              density="compact" />
          </Box>
        </Paper>
      )}

      {/* Tab 2: Estadísticas */}
      {tab === 2 && (
        <Box display="flex" flexDirection="column" gap={4}>

          {/* Sesiones por día */}
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={600} mb={2}>
              📈 Sesiones por día (últimos 14 días)
            </Typography>
            <Line data={lineData} options={{
              responsive: true,
              plugins: { legend: { display: false } },
              scales: { y: { beginAtZero: true, ticks: { stepSize: 1 } } },
            }} />
          </Paper>

          {/* Top 10 usuarios */}
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={600} mb={2}>
              🏆 TOP 10 usuarios (últimos 7 días)
            </Typography>
            <Bar data={barData} options={{
              responsive: true,
              indexAxis: 'y', // barras horizontales → más legible con nombres largos
              plugins: { legend: { display: false } },
              scales: { x: { beginAtZero: true, ticks: { stepSize: 1 } } },
            }} />
          </Paper>

          {/* Participación top 10 + Otros */}
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={600} mb={2}>
              🥧 Participación total (Top 10 + Otros)
            </Typography>
            <Box sx={{ maxWidth: 500, mx: 'auto' }}>
              <Doughnut data={pieData} options={{
                responsive: true,
                plugins: {
                  legend: {
                    position: 'right',
                    labels: { boxWidth: 12, font: { size: 11 } },
                  },
                  tooltip: {
                    callbacks: {
                      label: (ctx) => ` ${ctx.label}: ${ctx.parsed} sesiones`,
                    },
                  },
                },
              }} />
            </Box>
          </Paper>

        </Box>
      )}

      {/* Tab 3: Registro de usuarios */}
      {tab === 3 && (
        <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Box p={2} sx={{ borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="h6" fontWeight={600}>Usuarios registrados</Typography>
          </Box>
          <Box p={2}>
            <UsersTable />
          </Box>
        </Paper>
      )}

    </Container>
  );
}